// @ts-nocheck
import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
  const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
  const id = this.id;

  if (!productId || !id) {
    throw new Error('Product ID and an ID attribute are required');
  }

  // already loaded
  if (recommendationsPerformed === 'true') return;

  this.#fetchCachedRecommendations(productId, sectionId, intent)
    .then((result) => {
      if (!result.success) {
        if (!Shopify.designMode) {
          this.#handleError(new Error(`Server returned ${result.status}`));
        }
        return;
      }

      // ✅ Parse safely (no live DOM pollution)
      const parser = new DOMParser();
      const doc = parser.parseFromString(result.data || '', 'text/html');

      const recommendations = doc.querySelector(
        `product-recommendations[id="${id}"]`
      );

      if (!recommendations || !recommendations.innerHTML.trim().length) {
        this.#handleError(new Error('No recommendations available'));
        return;
      }

      this.dataset.recommendationsPerformed = 'true';

      const newContent = recommendations.querySelector('.recommended-products-grid');
      const currentContent = this.querySelector('.recommended-products-grid');

      if (!newContent || !currentContent) {
        this.#handleError(new Error('Missing recommendation grid'));
        return;
      }

      // ✅ Replace ONLY children (no wrapper duplication, less JS re-triggering)
      currentContent.replaceChildren(...newContent.children);

      // ✅ Visibility logic
      const numberOfProducts = currentContent.querySelectorAll('.product-card').length;

      if (numberOfProducts > 0) {
        this.classList.add('visible');
      } else {
        this.remove();
        return;
      }

      // ✅ Re-init slider safely (without stacking instances)
      const slider = this.querySelector('.slider-component');

      if (slider) {
        // destroy previous instance if exists
        if (slider.flkty) {
          slider.flkty.destroy();
          slider.flkty = null;
        }

        slider.init();
      }

    })
    .catch((e) => {
      this.#handleError(e);
    });
}

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
